import {useNavigate, useSearchParams} from "react-router-dom";
import {Container} from "@mantine/core";
import {
  pipelineGraphSelector,
  pipelineSelector,
  useGetPipelineGraphQuery,
  useGetPipelineQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {LoadingPage} from "../../components/page/LoadingPage/LoadingPage";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {PipelineGraphViewer} from "../../components/graph/PipelineGraphViewer/PipelineGraphViewer";

type Props = {
  id?: string;
}

export const PipelineGraphViewPageRoute = () => {
  const [params, _setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {t} = useTranslation()

  const id = params.get("id") ?? undefined;

  if (!id) {
    navigate(-1);
    return (
      <ErrorPage
        message={t("common.entity.pipelinegraph", "Pipeline Graph") + " " + t("common.error.notfound", "Not Found")}/>
    );
  }

  return <PipelineGraphViewPage id={id}/>;
};

export const PipelineGraphViewPage = (props: Props) => {
  const {id} = props;

  const {t} = useTranslation(["common"])

  const selectPipelineGraph = useMemo(() => {
    return pipelineGraphSelector()
  }, []);

  const {pipelineGraph, isFetching} = useGetPipelineGraphQuery(id ?? skipToken, {
    selectFromResult: (result) => ({
      pipelineGraph: selectPipelineGraph(result),
      isFetching: result.isFetching
    })
  });

  const selectPipeline = useMemo(() => {
    return pipelineSelector()
  }, [])

  const {pipeline} = useGetPipelineQuery(pipelineGraph?.pipelineId ?? skipToken, {
    selectFromResult: (result) => ({
      pipeline: selectPipeline(result),
    })
  });

  if (isFetching) {
    return (
      <LoadingPage/>
    );
  }

  if (!pipelineGraph) {
    return (
      <ErrorPage
        message={t("common.entity.pipelinegraph", "Pipeline Graph") + " " + t("common.error.notfound", "Not Found")}/>
    );
  }

  return (
    <ResizeableContent>
      {(size) => (
        <ThemedPage>
          {(classes) => (
            <Container h={size.height} className={classes.container} fluid={true} py="md">
              <PipelineGraphViewer
                pipelineGraph={pipelineGraph}
                pipeline={pipeline}/>
            </Container>
          )}
        </ThemedPage>
      )}
    </ResizeableContent>
  );
};

